import * as Sentry from "@sentry/react";
import { OhApi } from "@shared/libs/siayuda";
import { CaregiverUuid, ProclientUuid } from "@shared/libs/siayuda/oh-api";
import {
  CaregiverCandidateUuid,
  LegalEntity,
  LegalEntityUuid,
  LegalMode,
  PrescriberUuid,
  StructureUuid,
} from "hassibot/services_v2/common/types";
import { MainCacheKeys } from "hassibot/services_v2/static";

import { ALL_LEGAL_MODES, cacheConfiguration } from "./config";
import {
  ALL_ENTITY_STORES_NAMES,
  CacheEvictionStrategy,
  SimpleObjectStore,
} from "./simple-object-store";

type StoreNameType = ALL_ENTITY_STORES_NAMES;
type StoreKeyType =
  | CaregiverUuid
  | CaregiverCandidateUuid
  | StructureUuid
  | ProclientUuid
  | PrescriberUuid
  | MainCacheKeys;
type StoreValueType = unknown;
type StoresType = Record<StoreNameType, SimpleObjectStore<StoreKeyType, StoreValueType>>;
type LegalEntityDatabase = {
  legalEntityUuid: LegalEntityUuid;
  mode: LegalMode;
  stores: StoresType;
};

const isStoreNameValidForDatabase = (
  database: LegalEntityDatabase,
  storeName: string
): storeName is keyof typeof database.stores => {
  return Object.keys(database.stores).includes(storeName);
};

class IndexedDBCache {
  legalEntityDatabase: LegalEntityDatabase | null;

  constructor() {
    this.legalEntityDatabase = null;
  }

  initLegalEntityDatabase(legalEntity: LegalEntity): void {
    // Idempotent: callers re-run this from render. Recreating the stores for an
    // unchanged entity would drop their in-flight reentrant promises.
    if (
      this.legalEntityDatabase !== null &&
      this.legalEntityDatabase.legalEntityUuid === legalEntity.uuid &&
      this.legalEntityDatabase.mode === legalEntity.mode
    ) {
      return;
    }

    const stores = {} as StoresType;

    const filteredEntityStoresNames: ALL_ENTITY_STORES_NAMES[] = Object.values(
      ALL_ENTITY_STORES_NAMES
    ).filter(storeName =>
      [legalEntity.mode, ALL_LEGAL_MODES].includes(cacheConfiguration[storeName].mode)
    );

    filteredEntityStoresNames.forEach(storeName => {
      stores[storeName] = new SimpleObjectStore(
        storeName,
        legalEntity,
        filteredEntityStoresNames,
        cacheConfiguration[storeName].evictionStrategy
      );
    });

    this.legalEntityDatabase = {
      legalEntityUuid: legalEntity.uuid,
      mode: legalEntity.mode,
      stores: stores,
    };
  }

  /**
   * Updates the value of the given key in the indexedDB store `storeName`
   */
  set<CustomKeyType extends StoreKeyType, CustomValueType extends StoreValueType>(
    storeName: StoreNameType,
    key: CustomKeyType,
    value: CustomValueType
  ): Promise<void> {
    if (!this.legalEntityDatabase) {
      throw new Error("Legal entity database not defined.");
    }
    if (!isStoreNameValidForDatabase(this.legalEntityDatabase, storeName)) {
      throw new Error("Unknown store name for current legal entity database (wrong mode).");
    }

    const store = this.legalEntityDatabase.stores[storeName];
    return store.set(key, value);
  }

  /**
   * Fetches the value of the given key in the indexedDB store `storeName`.
   * If none if found, use the callback to fetch the value and store it in the indexedDB store.
   */
  getOrCreate<CustomKeyType extends StoreKeyType, CustomValueType = StoreValueType>(
    storeName: StoreNameType,
    key: CustomKeyType,
    callback: () => Promise<OhApi.ApiResponse<CustomValueType>>,
    evictionStrategy?: CacheEvictionStrategy
  ): Promise<OhApi.ApiResponse<CustomValueType>> {
    if (!this.legalEntityDatabase) {
      throw new Error("Legal entity database not defined.");
    }
    if (!isStoreNameValidForDatabase(this.legalEntityDatabase, storeName)) {
      throw new Error("Unknown store name for current legal entity database (wrong mode).");
    }

    const store = this.legalEntityDatabase.stores[storeName] as SimpleObjectStore<
      StoreKeyType,
      CustomValueType
    >;

    return store.getOrCreate(key, callback, evictionStrategy);
  }

  /**
   * Delete the given key from the indexedDB store `storeName`.
   */
  delete(storeName: StoreNameType, key: StoreKeyType): Promise<void> {
    if (!this.legalEntityDatabase) {
      throw new Error("Legal entity database not defined.");
    }
    if (!isStoreNameValidForDatabase(this.legalEntityDatabase, storeName)) {
      throw new Error("Unknown store name for current legal entity database (wrong mode).");
    }

    const store = this.legalEntityDatabase.stores[storeName];
    return store.del(key);
  }

  /**
   * Delete the given key like `delete`, but never fail: the failure is reported to Sentry.
   * `await` also catches the errors that `delete` throws before returning its promise.
   */
  async deleteAndReportFailure(storeName: StoreNameType, key: StoreKeyType): Promise<void> {
    try {
      await this.delete(storeName, key);
    } catch (error) {
      Sentry.withScope(scope => {
        scope.setExtra("Store name", storeName);
        scope.setExtra("Key", key);
        Sentry.captureException(error);
      });
    }
  }
}

export const indexedDBCache = new IndexedDBCache();
